import { useEffect, useState } from "react";
import api from "../../lib/api";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Save, Settings as SettingsIcon, FileText, Building2, Palette } from "lucide-react";
import PdfDesignDialog from "../../components/PdfDesignDialog";
import PrivacyCard from "../../components/PrivacyCard";
import AiCard from "../../components/AiCard";
import { toast } from "sonner";

export default function Settings() {
  const [form, setForm] = useState({
    default_pdf_note: "",
    company_name: "",
    company_phone: "",
    company_address: "",
    quote_footer_note: "",
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pdfOpen, setPdfOpen] = useState(false);
  const [vis, setVis] = useState({ enabled: true, hide_from: "22:00", show_from: "07:00", done_days: 7 });

  useEffect(() => {
    api.get("/settings").then(r => {
      setForm({
        default_pdf_note: r.data.default_pdf_note || "",
        company_name: r.data.company_name || "",
        company_phone: r.data.company_phone || "",
        company_address: r.data.company_address || "",
        quote_footer_note: r.data.quote_footer_note || "",
      });
      setVis((v) => ({ ...v, ...(r.data.tech_visibility || {}) }));
    }).finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings", { ...form, tech_visibility: { ...vis, done_days: Number(vis.done_days) || 7 } });
      toast.success("Ayarlar kaydedildi ✓");
    } catch (e) { toast.error(e.response?.data?.detail || "Hata"); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="text-slate-500">Yükleniyor...</div>;

  return (
    <div className="space-y-6 max-w-3xl" data-testid="settings-page">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 inline-flex items-center gap-1.5">
          <SettingsIcon className="h-3 w-3" /> Sistem Ayarları
        </div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight mt-1">Ayarlar</h1>
        <p className="text-sm text-slate-500 mt-1">Tüm PDF servis fişlerinde otomatik gösterilecek bilgiler.</p>
      </div>

      <Card className="p-6 border border-slate-200 shadow-none flex items-center justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2"><Palette className="h-4 w-4 text-blue-600" /><h3 className="font-display text-lg font-semibold text-slate-900">PDF Tasarımı</h3></div>
          <p className="text-sm text-slate-600 mt-1">Servis fişinin başlık, renk, bölüm adları ve görünen alanlarını canlı önizleme ile düzenleyin.</p>
        </div>
        <Button variant="outline" onClick={() => setPdfOpen(true)} data-testid="open-pdf-design"><Palette className="h-4 w-4 mr-1.5" /> PDF tasarlama ekranı</Button>
      </Card>
      <PdfDesignDialog open={pdfOpen} onOpenChange={setPdfOpen} />

      <Card className="p-6 border border-slate-200 shadow-none space-y-5">
        <div className="flex items-center gap-2">
          <FileText className="h-4 w-4 text-blue-600" />
          <h3 className="font-display text-lg font-semibold text-slate-900">Varsayılan PDF Notu</h3>
        </div>
        <p className="text-sm text-slate-600">
          Bu metin tüm servis fişlerinde, <strong>imzalar bölümünün hemen üstünde</strong> otomatik olarak basılır.
          Garanti şartları, müşteri taahhütleri, iletişim notları vb. için ideal.
        </p>
        <div>
          <Label htmlFor="note">Otomatik PDF Notu</Label>
          <Textarea
            id="note"
            rows={6}
            value={form.default_pdf_note}
            onChange={(e) => setForm({ ...form, default_pdf_note: e.target.value })}
            placeholder="Örn:&#10;• Yapılan işçilik 1 ay, kullanılan parçalar 6 ay garantilidir.&#10;• Cihazınızın yıllık bakımını ihmal etmeyiniz.&#10;• Şikayet ve önerileriniz için: 0555 555 55 55"
            data-testid="default-pdf-note-textarea"
            className="mt-1.5 font-mono text-sm"
          />
          <div className="text-xs text-slate-500 mt-1">{form.default_pdf_note.length} karakter • her satır PDF'te ayrı satırda görünür</div>
        </div>
      </Card>

      <Card className="p-6 border border-slate-200 shadow-none space-y-4">
        <div className="flex items-center gap-2">
          <Building2 className="h-4 w-4 text-emerald-600" />
          <h3 className="font-display text-lg font-semibold text-slate-900">Firma Bilgileri (opsiyonel)</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label>Firma Adı</Label>
            <Input value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} placeholder="ServisPlus" />
          </div>
          <div>
            <Label>Telefon</Label>
            <Input value={form.company_phone} onChange={(e) => setForm({ ...form, company_phone: e.target.value })} placeholder="0 312 ..." />
          </div>
          <div className="md:col-span-2">
            <Label>Adres</Label>
            <Input value={form.company_address} onChange={(e) => setForm({ ...form, company_address: e.target.value })} placeholder="Ankara / Türkiye" />
          </div>
        </div>
      </Card>

      <Card className="p-6 border border-slate-200 shadow-none space-y-4">
        <div className="flex items-center gap-2">
          <FileText className="h-4 w-4 text-amber-600" />
          <h3 className="font-display text-lg font-semibold text-slate-900">Fiyat Teklifi Alt Notu</h3>
        </div>
        <p className="text-sm text-slate-600">
          Teknisyenin oluşturduğu fiyat tekliflerinde varsayılan olarak görünür (teknisyen üzerine yazabilir).
          Müşteriye gösterilen ekranda ve PDF'te <strong>amber renkli uyarı kutusunda</strong> belirir.
        </p>
        <div>
          <Label>Varsayılan Alt Not</Label>
          <Textarea
            rows={3}
            value={form.quote_footer_note}
            onChange={(e) => setForm({ ...form, quote_footer_note: e.target.value })}
            placeholder="İndirim talepleriniz için lütfen Servis Müdürümüz ile iletişime geçiniz."
            data-testid="quote-footer-note-textarea"
            className="mt-1.5 font-mono text-sm"
          />
        </div>
      </Card>

      <PrivacyCard />
      <AiCard />
      <Card className="p-6 border border-slate-200 shadow-none space-y-4" data-testid="tech-visibility-card">
        <h3 className="font-display text-lg font-semibold text-slate-900">Usta Ekranı Gizlilik Kuralları</h3>
        <p className="text-sm text-slate-600">Müşteri bilgilerinin çalınmasını önlemek için: gece saatlerinde usta ekranında iş görünmez, tamamlanan işler belirli gün sonra ustanın ekranından kalkar (yönetici tekrar atarsa yeniden görünür).</p>
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!vis.enabled} onChange={(e) => setVis({ ...vis, enabled: e.target.checked })} data-testid="vis-enabled" /> Kuralları uygula</label>
        <div className="grid grid-cols-3 gap-3">
          <div><Label>Gizlenme saati</Label><Input type="time" value={vis.hide_from} onChange={(e) => setVis({ ...vis, hide_from: e.target.value })} data-testid="vis-hide" /></div>
          <div><Label>Yeniden görünme</Label><Input type="time" value={vis.show_from} onChange={(e) => setVis({ ...vis, show_from: e.target.value })} data-testid="vis-show" /></div>
          <div><Label>Tamamlanan iş (gün)</Label><Input type="number" min="1" value={vis.done_days} onChange={(e) => setVis({ ...vis, done_days: e.target.value })} data-testid="vis-days" /></div>
        </div>
      </Card>

      <Button onClick={save} disabled={saving} className="bg-blue-600 hover:bg-blue-700" data-testid="settings-save-button">
        <Save className="h-4 w-4 mr-2" /> {saving ? "Kaydediliyor..." : "Ayarları Kaydet"}
      </Button>
    </div>
  );
}
